import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, Star, AlertCircle, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DatePicker } from '../components/DatePicker';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Swiss-style currency formatter: CHF 1'280.99
const formatCHF = (value) => {
  const n = Number(value || 0);
  const fixed = n.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const withApostrophes = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `CHF ${withApostrophes}.${decPart}`;
};

const formatDateFR = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const isOverdue = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

// Star priority display (read-only)
const StarsDisplay = ({ value }) => {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={`stars-${v}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={16}
          className={i <= v ? 'fill-amber-400 text-amber-400' : 'fill-slate-200 text-slate-200'}
        />
      ))}
    </span>
  );
};

// Star priority picker (interactive)
const StarsPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1" data-testid="priority-picker">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        className="p-1 transition-transform hover:scale-110"
        data-testid={`priority-${i}`}
        aria-label={`Priorité ${i}`}
      >
        <Star
          size={22}
          className={i <= value ? 'fill-amber-400 text-amber-400' : 'fill-slate-200 text-slate-300'}
        />
      </button>
    ))}
    <span className="ml-2 text-sm text-slate-500 tabular-nums">{value}/5</span>
  </div>
);

const emptyForm = {
  client_id: '',
  detail: '',
  amount: '',
  deadline: '',
  priority: 3,
};

const Rappels = () => {
  const [reminders, setReminders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('Toutes');
  const [clientFilter, setClientFilter] = useState('Tous');

  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [remRes, cliRes] = await Promise.all([
        axios.get(`${API_URL}/reminders`),
        axios.get(`${API_URL}/clients`),
      ]);
      setReminders(remRes.data || []);
      setClients(cliRes.data || []);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (id) => {
    const c = clients.find((x) => x.id === id);
    return c ? c.company_name : '-';
  };

  const filteredReminders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return reminders
      .filter((r) => {
        if (priorityFilter !== 'Toutes' && Number(r.priority) !== Number(priorityFilter)) return false;
        if (clientFilter !== 'Tous' && r.client_id !== clientFilter) return false;
        if (!q) return true;
        const clientName = (getClientName(r.client_id) || '').toLowerCase();
        return clientName.includes(q) || (r.detail || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (a.deadline || '').localeCompare(b.deadline || ''));
  }, [reminders, searchQuery, priorityFilter, clientFilter, clients]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditMode(false);
    setSelected(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (rem) => {
    setEditMode(true);
    setSelected(rem);
    setFormData({
      client_id: rem.client_id,
      detail: rem.detail,
      amount: String(rem.amount),
      deadline: rem.deadline,
      priority: rem.priority,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Veuillez choisir un client');
      return;
    }
    if (!formData.detail.trim()) {
      toast.error('Le détail est obligatoire');
      return;
    }
    if (!formData.deadline) {
      toast.error('La deadline est obligatoire');
      return;
    }
    const amount = parseFloat(formData.amount);
    if (Number.isNaN(amount) || amount < 0) {
      toast.error('Le montant doit être un nombre positif');
      return;
    }
    try {
      const payload = {
        client_id: formData.client_id,
        detail: formData.detail.trim(),
        amount,
        deadline: formData.deadline,
        priority: Number(formData.priority),
      };
      if (editMode && selected) {
        await axios.put(`${API_URL}/reminders/${selected.id}`, payload);
        toast.success('Rappel mis à jour');
      } else {
        await axios.post(`${API_URL}/reminders`, payload);
        toast.success('Rappel créé');
      }
      setShowForm(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API_URL}/reminders/${selected.id}`);
      toast.success('Rappel supprimé');
      setShowDeleteConfirm(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rappels-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1>Rappels Clients</h1>
        <Button onClick={openCreate} data-testid="new-reminder-button">
          <Plus size={18} className="mr-2" />
          Nouveau rappel
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            type="text"
            placeholder="Rechercher par client ou par détail..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 h-11"
            data-testid="reminder-search"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger data-testid="reminder-filter-priority">
              <SelectValue placeholder="Priorité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Toutes">Toutes les priorités</SelectItem>
              {[1, 2, 3, 4, 5].map((p) => (
                <SelectItem key={p} value={String(p)}>{`Priorité ${p}`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="reminder-filter-client">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Tous">Tous les clients</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {filteredReminders.length === 0 ? (
          <div className="empty-state">
            <Bell size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucun rappel</p>
            <p className="text-sm">Créez un rappel pour suivre les relances clients</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="reminders-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Détail</th>
                  <th className="text-right">Montant</th>
                  <th>Deadline</th>
                  <th>Priorité</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReminders.map((r) => {
                  const overdue = isOverdue(r.deadline);
                  return (
                    <tr key={r.id} data-testid={`reminder-row-${r.id}`}>
                      <td className="font-medium">{getClientName(r.client_id)}</td>
                      <td>
                        <div className="whitespace-pre-line text-slate-700">{r.detail}</div>
                      </td>
                      <td className="text-right tabular-nums font-medium">{formatCHF(r.amount)}</td>
                      <td className={overdue ? 'text-red-500 font-medium' : ''}>
                        {overdue ? (
                          <span className="inline-flex items-center gap-1">
                            <AlertCircle size={14} />
                            {formatDateFR(r.deadline)}
                          </span>
                        ) : (
                          formatDateFR(r.deadline)
                        )}
                      </td>
                      <td>
                        <StarsDisplay value={r.priority} />
                      </td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(r)}
                            data-testid={`edit-reminder-${r.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelected(r); setShowDeleteConfirm(true); }}
                            className="text-red-500"
                            data-testid={`delete-reminder-${r.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg" data-testid="reminder-form-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier le rappel' : 'Nouveau rappel'}</DialogTitle>
            <DialogDescription>
              {editMode ? "Modifiez les informations du rappel" : "Saisissez les informations pour créer un rappel"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                <SelectTrigger data-testid="reminder-form-client">
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Détail</Label>
              <Textarea
                value={formData.detail}
                onChange={(e) => setFormData({ ...formData, detail: e.target.value })}
                placeholder="Ex: Facture 260001 — Comptabilité Mars 2026"
                rows={3}
                data-testid="reminder-form-detail"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant (CHF)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="reminder-form-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Deadline</Label>
                <DatePicker
                  value={formData.deadline}
                  onChange={(v) => setFormData({ ...formData, deadline: v })}
                  testId="reminder-form-deadline"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Priorité</Label>
              <StarsPicker
                value={Number(formData.priority) || 0}
                onChange={(v) => setFormData({ ...formData, priority: v })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                Annuler
              </Button>
              <Button type="submit" data-testid="reminder-form-submit">
                {editMode ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce rappel ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Le rappel sera supprimé de la liste.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive" data-testid="confirm-delete-reminder">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Rappels;
